import { API_CONFIG } from '../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AnalyticsResponse, DemoRun, HistorySummary, JudgeCriteria, ModelConfig } from '../types';

export class ApiService {

  /**
   * Get a fresh ID token from Amplify, which auto-refreshes expired tokens.
   */
  private async getToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * @deprecated Use getToken() instead. Kept for WebSocket init compatibility.
   */
  setAuthToken(_token: string): void {
    // No-op — tokens are now fetched fresh on each request
  }

  private async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const baseUrl = API_CONFIG.REST_API_URL.replace(/\/+$/, '');
    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`API Error: ${response.status} - ${error}`);
    }

    return response;
  }

  /**
   * Create a new demo run
   */
  async createDemoRun(testId: number, userId: string, customPrompt?: string, webSearch?: boolean): Promise<{
    demoRunId: string;
    queuePosition: number;
    estimatedWaitTime: number;
    modelIds?: string[];
  }> {
    const body: Record<string, any> = { testId, userId };
    if (customPrompt) {
      body.customPrompt = customPrompt;
    }
    if (webSearch !== undefined) {
      body.webSearch = webSearch;
    }
    const response = await this.fetch('/demo-run', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json();
  }

  /**
   * Get user's demo run history
   */
  async getHistory(userId: string, limit: number = 20, lastKey?: string): Promise<{
    runs: HistorySummary[];
    lastKey?: string;
  }> {
    const params = new URLSearchParams({ userId, limit: limit.toString() });
    if (lastKey) {
      params.append('lastKey', lastKey);
    }
    const response = await this.fetch(`/history?${params.toString()}`);
    return response.json();
  }

  /**
   * Get full details of a specific demo run
   */
  async getDemoRun(demoRunId: string): Promise<DemoRun> {
    const response = await this.fetch(`/history/${demoRunId}`);
    return response.json();
  }

  /**
   * Get full details of a specific demo run (alias for getDemoRun)
   */
  async getDemoRunDetail(demoRunId: string): Promise<DemoRun> {
    return this.getDemoRun(demoRunId);
  }

  /**
   * Trigger Round 2 rating for a demo run (async — returns 202 immediately)
   */
  async triggerRound2Rating(demoRunId: string): Promise<{
    demoRunId: string;
    message: string;
  }> {
    const response = await this.fetch(`/demo-run/${demoRunId}/rate`, {
      method: 'POST',
    });
    return response.json();
  }

  /**
   * Get all model configurations (admin only)
   */
  async getModels(): Promise<{ models: ModelConfig[] }> {
    const response = await this.fetch('/admin/models');
    return response.json();
  }

  /**
   * Update model enabled/disabled status (admin only)
   */
  async updateModelStatus(modelId: string, enabled: boolean): Promise<ModelConfig> {
    const response = await this.fetch(`/admin/models/${modelId}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
    return response.json();
  }

  /**
   * Update API key for a provider (admin only)
   */
  async updateApiKey(provider: string, apiKey: string): Promise<{
    provider: string;
    updated: boolean;
  }> {
    const response = await this.fetch(`/admin/api-keys/${provider}`, {
      method: 'PUT',
      body: JSON.stringify({ apiKey }),
    });
    return response.json();
  }

  /**
   * Test API connection for a provider (admin only)
   */
  async testConnection(provider: string): Promise<{
    provider: string;
    success: boolean;
    message: string;
  }> {
    const response = await this.fetch(`/admin/api-keys/${provider}/test`, {
      method: 'POST',
    });
    return response.json();
  }

  /**
   * Get analytics data for admin dashboard (admin only)
   */
  async getAnalytics(startDate?: string, endDate?: string): Promise<AnalyticsResponse> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const qs = params.toString();
    const response = await this.fetch(`/admin/analytics${qs ? `?${qs}` : ''}`);
    return response.json();
  }

  async getJudgeCriteria(): Promise<Record<string, JudgeCriteria>> {
    const response = await this.fetch('/admin/judge');
    return response.json();
  }

  async updateJudgeCriteria(testId: number, criteria: string): Promise<void> {
    await this.fetch('/admin/judge', {
      method: 'PUT',
      body: JSON.stringify({ testId, criteria }),
    });
  }

}

// Singleton instance
export const apiService = new ApiService();
